import React, { useCallback, useContext, useMemo, useRef } from "react";

import { selectedMerchantStorageKey } from "merchant-portal-frontend/src/global.constants";
import { useLocalStorage } from "merchant-portal-frontend/src/utils/localStorage";

import { useAuth } from "./AuthProvider";

export const unassignedMerchantID = "00000000-0000-0000-0000-000000000000";

export interface MerchantContextValue {
	selectedMerchantID: string | null;
	updateSelectedMerchantID: (selectedMerchantID: string | null) => Promise<void>;
	hasAssignedMerchant: boolean;
	setValidationFunction: (callbackFn: ValidationFunction) => void;
}

export const MerchantContext = React.createContext<MerchantContextValue>({
	selectedMerchantID: null,
	updateSelectedMerchantID: () => Promise.resolve(),
	hasAssignedMerchant: true,
	setValidationFunction: () => {},
});

type ValidationFunction = (merchantID: string | null) => boolean;

const MerchantProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { breadToken } = useAuth();

	const validationRef = useRef<ValidationFunction>();

	const assignedMerchant = useMemo(() => breadToken?.claims?.merchantID || "", [breadToken]);
	const hasAssignedMerchant = useMemo(
		() => assignedMerchant !== unassignedMerchantID,
		[assignedMerchant],
	);

	const [storedMerchantID, setStoredSelectedMerchantID] = useLocalStorage<string | null>(
		selectedMerchantStorageKey,
		null,
	);

	const selectedMerchantID = useMemo(
		() => (hasAssignedMerchant ? assignedMerchant : storedMerchantID),
		[assignedMerchant, hasAssignedMerchant, storedMerchantID],
	);

	const updateSelectedMerchantID = useCallback(
		async (newSelectedMerchantID: string | null) => {
			if (validationRef.current && !validationRef.current(newSelectedMerchantID)) {
				return;
			}
			if (!hasAssignedMerchant) {
				setStoredSelectedMerchantID(newSelectedMerchantID);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const setValidationFunction = (callbackFn: ValidationFunction) => {
		validationRef.current = callbackFn;
	};

	return (
		<MerchantContext.Provider
			value={{
				selectedMerchantID,
				updateSelectedMerchantID,
				hasAssignedMerchant,
				setValidationFunction,
			}}
		>
			{children}
		</MerchantContext.Provider>
	);
};

export default MerchantProvider;

export const useMerchant = () => {
	const context = useContext(MerchantContext);
	if (context === undefined) {
		throw new Error("useMerchant must be used within a MerchantProvider");
	}
	return context;
};
